import { Chip, FormControl, InputLabel } from '@material-ui/core';
import { format } from 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import { primaryBlue, red } from 'utils/color-styles';
import { currencyFormat } from 'utils/currencyFormat';
import DonationMatchingRule from 'entities/donationMatchingRule/model';

const DonationMatchingRuleCompact = ({
  donationMatchingRule,
  budgetInactive,
  showContributionAmount,
}: {
  donationMatchingRule: DonationMatchingRule;
  budgetInactive: boolean;
  showContributionAmount: boolean;
}): JSX.Element => {
  const now = new Date();
  const startingAt = donationMatchingRule.get('startingAt');
  const endingAt = donationMatchingRule.get('endingAt');

  const balance = donationMatchingRule.get('limitTotal')
    ? donationMatchingRule.get('limitTotal') - donationMatchingRule.get('totalIssued')
    : undefined;

  const inactive =
    budgetInactive || (endingAt && endingAt < now) || (startingAt && startingAt > now);
  const balanceLow =
    donationMatchingRule.get('limitTotal') &&
    !inactive &&
    balance / donationMatchingRule.get('limitTotal') < 0.15;

  const opacity = inactive ? '0.5' : '1';
  const columnWidth = showContributionAmount ? '12%' : '14%';
  return (
    <div
      style={{
        border: `1px solid rgba(0, 0, 0, ${inactive ? '0.1' : '0.2'})`,
        padding: '10px 30px 15px 30px',
        marginBottom: 30,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            opacity,
          }}
        >
          <h3>{donationMatchingRule.get('title')}</h3>
          <Chip color="primary" label="Matching" style={{ marginLeft: 20 }} />
          <Chip label={donationMatchingRule.get('currencyCode')} style={{ marginLeft: 10 }} />
        </div>
        <Link
          to={`/donations/matching-rule/${donationMatchingRule.get('id')}`}
          style={{
            color: primaryBlue,
            textDecoration: 'underline',
            opacity: budgetInactive ? '0.5' : 1,
          }}
        >
          Edit
        </Link>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 15,
          opacity,
        }}
      >
        <div style={{ width: columnWidth }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Used</InputLabel>
          </FormControl>
          <div>
            {currencyFormat(
              donationMatchingRule.get('totalIssued'),
              donationMatchingRule.get('currencyCode')
            )}
          </div>
        </div>
        <div style={{ width: columnWidth, color: balanceLow ? red : undefined }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Balance</InputLabel>
          </FormControl>
          <div>
            {donationMatchingRule.get('limitTotal')
              ? currencyFormat(
                  donationMatchingRule.get('limitTotal') - donationMatchingRule.get('totalIssued'),
                  donationMatchingRule.get('currencyCode')
                )
              : '-'}
          </div>
        </div>
        <div style={{ width: columnWidth }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Total Limit</InputLabel>
          </FormControl>
          <div>
            {donationMatchingRule.get('limitTotal')
              ? currencyFormat(
                  donationMatchingRule.get('limitTotal'),
                  donationMatchingRule.get('currencyCode')
                )
              : '-'}
          </div>
        </div>
        <div style={{ width: columnWidth }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Limit Per User</InputLabel>
          </FormControl>
          <div>
            {donationMatchingRule.get('limitPerUser')
              ? currencyFormat(
                  donationMatchingRule.get('limitPerUser'),
                  donationMatchingRule.get('currencyCode')
                )
              : '-'}
          </div>
        </div>
        {showContributionAmount && (
          <div style={{ width: columnWidth }}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Contributions Matched</InputLabel>
            </FormControl>
            <div>
              {currencyFormat(
                donationMatchingRule.get('totalContribution'),
                donationMatchingRule.get('currencyCode')
              )}
            </div>
          </div>
        )}
        <div style={{ width: columnWidth }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Matching Percentage</InputLabel>
          </FormControl>
          <div>{donationMatchingRule.get('matchingPercentage')}%</div>
        </div>
        <div style={{ width: showContributionAmount ? '13%' : '15%' }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Donations Matched</InputLabel>
          </FormControl>
          <div>{donationMatchingRule.get('donationsMatchedCount') || 0}</div>
        </div>
        <div style={{ width: '15%' }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Start &amp; End Date</InputLabel>
          </FormControl>
          <div>
            {startingAt || endingAt ? (
              <>
                {startingAt ? format(legacyParse(startingAt), convertTokens('YYYY-MM-DD')) : 'N/A'}
                {' – '}
                {endingAt ? format(legacyParse(endingAt), convertTokens('YYYY-MM-DD')) : 'N/A'}
              </>
            ) : (
              'Not constrained'
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationMatchingRuleCompact;
